import { useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { ANNOTATION_COLORS, STAMP_NAMES, stampColor, userColor } from '../types';
import type { PresenceEntry, ToastMessage } from '../sync';

// ---------------------------------------------------------------------------
// Icons (inline SVG, stroke = currentColor)
// ---------------------------------------------------------------------------

const PATHS: Record<string, string> = {
  back: 'M15 18l-6-6 6-6',
  prev: 'M15 18l-6-6 6-6',
  next: 'M9 18l6-6-6-6',
  close: 'M6 6l12 12M18 6L6 18',
  refresh: 'M20 11a8 8 0 1 0-2.3 5.7M20 4v7h-7',
  drive: 'M8 3h8l6 11-4 7H6l-4-7z M8 3l8 18 M16 3L6 21',
  folder: 'M3 7a2 2 0 0 1 2-2h4l2 2h6a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
  file: 'M6 2h8l4 4v16H6z M14 2v4h4',
  check: 'M5 12l4 4 10-10',
  cloud: 'M7 18a5 5 0 1 1 1-9.9A6 6 0 0 1 19 10a4 4 0 0 1 0 8z',
  panel: 'M3 4h18v16H3z M15 4v16',
  sidebar: 'M3 4h18v16H3z M9 4v16',
  signout: 'M15 4h4v16h-4 M10 8l-4 4 4 4 M6 12h10',
  send: 'M4 12l16-8-6 16-2-6z',
  plus: 'M12 5v14M5 12h14',
  pin: 'M12 21s-6-5.5-6-11a6 6 0 0 1 12 0c0 5.5-6 11-6 11z M12 10h0',
};

export function Icon({ name, size = 16, title }: { name: keyof typeof PATHS | string; size?: number; title?: string }) {
  return (
    <svg className="rl-ic" viewBox="0 0 24 24" width={size} height={size} aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title ? <title>{title}</title> : null}
      <path d={PATHS[name] ?? PATHS.file} />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Toasts — module-level store so anything (sync events, panels) can toast.
// ---------------------------------------------------------------------------

export interface Toast extends ToastMessage {
  id: number;
}

let toasts: Toast[] = [];
let nextToastId = 1;
const toastListeners = new Set<() => void>();
const emitToasts = () => toastListeners.forEach((l) => l());

export function toast(message: string, kind: ToastMessage['kind'] = 'info', ms = 3200): number {
  const id = nextToastId++;
  toasts = [...toasts.filter((t) => t.message !== message), { id, message, kind }].slice(-4);
  emitToasts();
  if (ms > 0) setTimeout(() => dismissToast(id), ms);
  return id;
}
export function dismissToast(id: number): void {
  toasts = toasts.filter((t) => t.id !== id);
  emitToasts();
}

/** Renders the toast stack. Mount once (EditorShell and the app shells do). */
export function ToastHost() {
  const list = useSyncExternalStore(
    (cb) => {
      toastListeners.add(cb);
      return () => toastListeners.delete(cb);
    },
    () => toasts,
  );
  return (
    <div className="rl-toasts" role="status" aria-live="polite">
      {list.map((t) => (
        <div key={t.id} className="rl-toast" data-kind={t.kind}>
          <span>{t.message}</span>
          <button aria-label="Dismiss" onClick={() => dismissToast(t.id)}>
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Avatars / presence
// ---------------------------------------------------------------------------

export function initials(name: string, email = ''): string {
  const src = name.trim() || email.trim();
  if (!src) return '··';
  const parts = src.split(/[\s@.]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

export function Avatar({ name, id, title }: { name: string; id?: string; title?: string }) {
  return (
    <span className="rl-avatar" title={title ?? name} style={{ background: id ? userColor(id) : undefined }}>
      {initials(name)}
    </span>
  );
}

export function PresenceAvatars({ users, max = 5, includeSelf = true }: { users: PresenceEntry[]; max?: number; includeSelf?: boolean }) {
  const list = (includeSelf ? users : users.filter((u) => !u.self)).sort((a, b) => Number(b.self) - Number(a.self));
  const shown = list.slice(0, max);
  return (
    <div className="rl-presence" aria-label={`${list.length} people here`}>
      {shown.map((u) => (
        <span key={u.user_id} className="rl-avatar" style={{ background: u.color }} title={u.self ? `${u.user_name} (you)` : u.user_name}>
          {initials(u.user_name)}
        </span>
      ))}
      {list.length > max ? <span className="rl-presence-more">+{list.length - max}</span> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Colour palette / stamp picker
// ---------------------------------------------------------------------------

export function ColorPalette({
  value,
  onChange,
  colors = ANNOTATION_COLORS,
  allowNone = false,
  label,
}: {
  value: string | null;
  onChange(color: string | null): void;
  colors?: readonly string[];
  allowNone?: boolean;
  label?: string;
}) {
  return (
    <div role="radiogroup" aria-label={label ?? 'Colour'} className="rl-swatches">
      {allowNone ? (
        <button
          type="button"
          role="radio"
          aria-checked={!value}
          className="rl-swatch"
          data-none=""
          data-active={!value ? '' : undefined}
          title="None"
          onClick={() => onChange(null)}
        />
      ) : null}
      {colors.map((c) => (
        <button
          type="button"
          role="radio"
          aria-checked={value?.toUpperCase() === c.toUpperCase()}
          key={c}
          className="rl-swatch"
          style={{ background: c }}
          data-active={value?.toUpperCase() === c.toUpperCase() ? '' : undefined}
          title={c}
          onClick={() => onChange(c)}
        />
      ))}
    </div>
  );
}

export function StampPicker({ value, onChange, names = STAMP_NAMES }: { value: string | null; onChange(name: string): void; names?: readonly string[] }) {
  return (
    <div className="rl-stamps" role="radiogroup" aria-label="Stamp">
      {names.map((name) => (
        <button
          type="button"
          role="radio"
          aria-checked={value === name}
          key={name}
          className="rl-stamp"
          style={{ color: stampColor(name) }}
          data-active={value === name ? '' : undefined}
          onClick={() => onChange(name)}
        >
          {name}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

export function Modal({
  title,
  onClose,
  children,
  footer,
  size,
  testId,
}: {
  title: string;
  onClose(): void;
  children: ReactNode;
  footer?: ReactNode;
  /** 'wide': about 90vw, up to 1100px (the Drive column browser). */
  size?: 'wide';
  testId?: string;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="rl-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rl-modal" role="dialog" aria-modal="true" aria-label={title} data-size={size} data-testid={testId}>
        <div className="rl-modal-head">
          <h3>{title}</h3>
          <button className="rl-icon-btn" aria-label="Close" onClick={onClose}>
            <Icon name="close" />
          </button>
        </div>
        <div className="rl-modal-body">{children}</div>
        {footer ? <div className="rl-modal-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Time formatting
// ---------------------------------------------------------------------------

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = (Date.now() - t) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function clockTime(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}
